import { OverrideToken, AliasToken } from 'antd/es/theme/interface';

const disabledFieldBg = 'var(--field-disabled-bg, var(--clr-container-bg))';

const focusHalo = '0 0 0 3px rgba(var(--clr-general-primary-rgb), 0.18)';

export const LARGE_BUTTON_HEIGHT = 36;

const fieldStateTokens = {
  colorBgContainer: 'var(--clr-container-bg)',
  activeBg: 'var(--clr-container-bg)',
  hoverBg: 'var(--clr-general-hover)',
  colorBorder: 'var(--clr-border-grey-100)',
  activeBorderColor: 'var(--clr-general-primary)',
  hoverBorderColor: 'var(--clr-general-border-hover)',
  activeShadow: focusHalo,
  errorActiveShadow: 'none',
  warningActiveShadow: 'none',
  colorError: 'var(--clr-error-border)',
  colorErrorBorderHover: 'var(--clr-error-border)',
  colorWarning: 'var(--clr-inline-warning)',
  colorWarningBorderHover: 'var(--clr-inline-warning)',
  colorBgContainerDisabled: disabledFieldBg,
  colorTextDisabled: 'var(--clr-title)',
  colorTextPlaceholder: 'var(--clr-general-description)',
  colorText: 'var(--clr-title)',
};

export const globalTokens: Partial<AliasToken> = {
  fontFamily: "'Poppins', sans-serif",
  lineWidthFocus: 2,
  colorPrimaryBorder: 'var(--clr-general-primary)',
  controlOutline: 'var(--primary-200)',
  controlOutlineWidth: 1,
  borderRadius: 2,
  colorText: 'var(--neutral-950)',
  colorPrimary: 'var(--primary-500)',
  colorPrimaryBg: 'var(--primary-50)',
  colorPrimaryBgHover: 'var(--primary-100)',
  colorPrimaryBorderHover: 'var(--primary-300)',
  colorPrimaryHover: 'var(--primary-300)',
  colorPrimaryActive: 'var(--primary-600)',
  colorPrimaryTextHover: 'var(--primary-400)',
  colorPrimaryText: 'var(--primary-500)',
  colorPrimaryTextActive: 'var(--primary-600)',
};

export const componentTokens: OverrideToken = {
  Button: {
    colorPrimary: 'var(--clr-general-primary)',
    primaryColor: 'var(--clr-white)',
    colorPrimaryBorder: 'var(--clr-general-primary)',
    colorPrimaryBorderHover: 'var(--clr-primary-hover)',
    defaultShadow: 'none',
    primaryShadow: 'none',
    dangerShadow: 'none',
    defaultActiveBg: 'var(--selected)',
    defaultActiveBorderColor: 'var(--clr-general-primary)',
    defaultActiveColor: 'var(--clr-general-primary)',
    defaultBg: 'var(--clr-container-bg)',
    defaultBorderColor: 'var(--clr-border-grey-100)',
    defaultColor: 'var(--clr-subtitle)',
    defaultHoverBg: 'var(--clr-general-hover)',
    defaultHoverBorderColor: 'var(--clr-general-border-hover)',
    defaultHoverColor: 'var(--clr-general-primary)',
    colorPrimaryHover: 'var(--clr-primary-hover)',
    borderRadius: 5,
    fontWeight: 500,
    controlHeightLG: LARGE_BUTTON_HEIGHT,
    controlHeight: 30,
    fontSize: 13,
    contentFontSizeLG: 13,
    borderRadiusLG: 5,
    paddingBlockLG: 10,
    paddingInlineLG: 20,
    colorBgContainer: 'var(--clr-container-bg)',
    colorError: 'var(--clr-primary-error)',
    colorErrorBorderHover: 'var(--clr-primary-light)',
    colorErrorHover: 'var(--clr-default-hover)',
    colorBgContainerDisabled: 'rgba(var(--clr-general-primary-rgb), 0.4)',
    borderColorDisabled: 'rgba(var(--clr-general-primary-rgb), 0)',
    colorTextDisabled: 'var(--clr-white)',
    textTextColor: 'var(--clr-general-primary)',
    textTextHoverColor: 'var(--clr-primary-light)',
    textTextActiveColor: 'var(--clr-primary-light)',
    textHoverBg: 'transparent',
    colorPrimaryTextHover: 'var(--clr-general-primary)',
  },

  Form: {
    labelColor: 'var(--clr-title)',
    labelFontSize: 12,
    labelRequiredMarkColor: 'var(--clr-primary-error)',
    labelHeight: 18,
    verticalLabelPadding: '0 0 5px 0',
    fontFamily: "'Poppins', sans-serif",
    itemMarginBottom: 0,
  },
  Input: {
    ...fieldStateTokens,
    colorPrimaryActive: 'var(--clr-general-primary)',
    paddingBlock: 12,
    paddingInline: 10,
    paddingBlockSM: 8,
    paddingInlineSM: 10,
    borderRadius: 5,
    borderRadiusLG: 5,
    borderRadiusSM: 5,
    inputFontSize: 13,
    inputFontSizeLG: 13,
    controlHeight: 30,
    controlHeightLG: 44,
    controlHeightSM: 30,
  },

  Radio: {
    colorPrimary: 'var(--clr-general-primary)',
    colorBorder: 'var(--clr-border-grey-100)',
    colorPrimaryBgHover: 'var(--clr-general-primary)',
    colorBgContainer: 'var(--clr-container-bg)',
    radioSize: 18,
    dotSize: 10,
  },
  Spin: {
    colorWhite: 'var(--clr-general-primary)',
    colorPrimary: 'var(--clr-general-primary)',
    zIndexPopupBase: 99999,
  },
  Modal: {
    contentBg: 'var(--clr-container-bg)',
    borderRadiusLG: 10,
    zIndexPopupBase: 1100,
    headerBg: 'var(--clr-container-bg)',
    colorBgMask: 'rgba(var(--clr-black-rgb), 0.5)',
  },
  Switch: {
    handleSize: 16,
    trackHeight: 20,
    trackMinWidth: 40,
    handleBg: 'var(--clr-white)',
    colorTextQuaternary: 'var(--clr-border-grey-100)',
    colorPrimary: 'var(--clr-general-primary)',
    colorPrimaryHover: 'var(--clr-general-primary)',
    colorTextTertiary: 'var(--clr-border-grey-100)',
    handleShadow: '0px 1px 2px 0px rgba(var(--clr-black-rgb), 0.25)',
  },
  Dropdown: {
    colorBgElevated: 'var(--clr-container-bg)',
    paddingBlock: 10,
    borderRadiusLG: 10,
    paddingXXS: 10,
    controlPaddingHorizontal: 10,
    boxShadowSecondary: '0px 0px 30px 0px var(--clr-shadow)',
    borderRadiusSM: 5,
  },
  Tabs: {
    inkBarColor: 'var(--clr-general-primary)',
    itemSelectedColor: 'var(--clr-title)',
    itemColor: 'var(--clr-general-description)',
    itemHoverColor: 'var(--clr-general-primary)',
    colorBorderSecondary: 'var(--clr-border-grey-100)',
  },
  Popover: {
    colorBgElevated: 'var(--clr-container-bg)',
    borderRadiusXS: 5,
    boxShadowSecondary: '0px 0px 30px 0px var(--clr-shadow)',
    borderRadiusLG: 5,
  },
  Checkbox: {
    colorBgContainer: 'var(--clr-container-bg)',
    colorBorder: 'var(--clr-border-grey-100)',
    colorPrimary: 'var(--clr-general-primary)',
    colorPrimaryBorder: 'var(--clr-general-primary)',
    controlInteractiveSize: 18,
    borderRadiusSM: 5,
    colorPrimaryHover: 'var(--clr-general-primary)',
  },
  Divider: {
    colorSplit: 'var(--clr-border-grey-100)',
    marginLG: 0,
  },
  Tooltip: {
    colorBgSpotlight: 'var(--clr-subtitle)',
    colorTextLightSolid: 'var(--clr-container-bg)',
    fontSize: 10,
    borderRadius: 5,
    borderRadiusXS: 5,
    boxShadowSecondary: 'none',
    paddingSM: 10,
    paddingXS: 10,
    controlHeight: 19,
    zIndexPopupBase: 1101,
  },
  InputNumber: {
    ...fieldStateTokens,
    paddingBlock: 11,
    paddingInline: 10,
    paddingBlockSM: 8,
    paddingInlineSM: 10,
    borderRadius: 5,
    borderRadiusLG: 5,
    borderRadiusSM: 5,
    inputFontSize: 13,
    controlHeight: 44,
    controlHeightLG: 44,
    controlHeightSM: 44,
  },
  DatePicker: {
    ...fieldStateTokens,
    paddingBlock: 11,
    paddingInline: 10,
    paddingBlockSM: 8,
    paddingInlineSM: 10,
    borderRadius: 5,
    borderRadiusLG: 5,
    borderRadiusSM: 5,
    inputFontSize: 13,
    inputFontSizeLG: 13,
    fontSize: 13,
    fontSizeLG: 13,
    inputFontSizeSM: 13,
    controlHeight: 45,
    controlHeightLG: 44,
    controlHeightSM: 45,
    cellHoverBg: 'var(--clr-general-hover)',
    cellHeight: 34,
    cellWidth: 38,
    colorSplit: 'var(--clr-border-grey-100)',
    paddingXXS: 2,
    colorPrimary: 'var(--clr-general-primary)',
    colorIcon: 'var(--clr-general-description)',
    colorIconHover: 'var(--clr-general-primary)',
    colorBgElevated: 'var(--clr-container-bg)',
    colorTextHeading: 'var(--clr-title)',
  },
  Table: {
    borderColor: 'var(--clr-border-grey-100)',
    headerBg: 'var(--clr-general-hover)',
    headerColor: 'var(--clr-title)',
    rowHoverBg: 'var(--clr-general-hover)',
    colorBgContainer: 'var(--clr-container-bg)',
    colorSplit: 'rgba(var(--clr-general-dark-grey-rgb), 0.15)',
    cellPaddingBlock: 10,
    cellPaddingInline: 10,
    headerBorderRadius: 4,
  },
  Slider: {
    handleActiveColor: 'var(--clr-general-primary)',
    handleActiveOutlineColor: 'rgba(var(--clr-general-primary-rgb), 0.2)',
    handleColor: 'rgba(var(--clr-general-primary-rgb), 0.8)',
    railBg: 'var(--clr-border-grey-100)',
    railHoverBg: 'var(--clr-border-grey-100)',
    trackBg: 'var(--clr-general-primary)',
    trackHoverBg: 'var(--clr-primary-hover)',
    borderRadiusXS: 4,
    colorPrimaryBorderHover: 'var(--clr-general-primary)',
    railSize: 5,
  },
};
